import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu as MenuIcon, X, ShoppingBag, Sparkles } from "lucide-react";
import { RESTAURANT } from "../data/restaurant";
import { useCart } from "../context/CartContext";
import Logo from "./Logo";
import RewardsModal from "./RewardsModal";
import OpenNowBadge from "./OpenNowBadge";

const links = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: "/feasts", label: "Feasts" },
  { to: "/about", label: "Story" },
  { to: "/contact", label: "Visit" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [rewardsOpen, setRewardsOpen] = useState(false);
  const location = useLocation();
  const { count, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`!fixed top-3 left-3 right-3 md:top-4 md:left-6 md:right-6 z-50 rounded-3xl transition-all duration-500 overflow-hidden ${
        scrolled
          ? "bg-[#0E0E1A]/60 backdrop-blur-[28px] backdrop-saturate-[1.6] border border-white/20 shadow-[0_24px_60px_-22px_rgba(14,14,26,0.55),inset_0_1px_0_rgba(255,255,255,0.22)]"
          : "bg-[#0E0E1A]/45 backdrop-blur-2xl backdrop-saturate-[1.4] border border-white/15"
      }`}
    >
      {/* glass top sheen */}
      <div className="absolute inset-0 pointer-events-none rounded-3xl bg-gradient-to-b from-white/10 to-transparent" aria-hidden />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 h-[68px] md:h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group">
          <Logo className="h-14 md:h-16 w-auto -my-2" />
          <OpenNowBadge variant="dark" className="hidden lg:inline-flex" />
        </Link>

        <nav className="hidden md:flex items-center gap-7" data-testid="primary-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[15px] font-extrabold tracking-wide transition-all drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)] ${
                  isActive
                    ? "text-[#E5B07D]"
                    : "text-white hover:text-[#E5B07D]"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            href={`tel:${RESTAURANT.phoneRaw}`}
            data-testid="header-call-btn"
            className="hidden lg:inline-flex items-center gap-2 text-sm font-semibold text-white/85 hover:text-[#E5B07D] transition-colors"
          >
            <Phone className="w-4 h-4" />
            {RESTAURANT.phone}
          </a>

          <button
            data-testid="header-rewards-btn"
            onClick={() => setRewardsOpen(true)}
            className="glass-pill hidden sm:inline-flex items-center gap-1.5 text-xs font-bold text-[#E5B07D] hover:text-white transition-all px-3.5 py-2 rounded-full"
          >
            <Sparkles className="w-3.5 h-3.5" /> Rewards
          </button>

          <button
            data-testid="header-cart-btn"
            onClick={() => setCartOpen(true)}
            className="glass-pill relative p-2.5 rounded-full hover:scale-105 transition-all"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-4 h-4 text-white" />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1 -right-1 bg-[#C9853E] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>

          <Link
            to="/menu"
            data-testid="header-order-now-btn"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C9853E] to-[#E5B07D] hover:from-[#A6692D] hover:to-[#C9853E] text-white rounded-full px-5 md:px-6 py-2.5 text-sm font-bold tracking-wide transition-all shadow-[0_6px_18px_-4px_rgba(201,133,62,0.55)] hover:-translate-y-0.5"
          >
            Order Now
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-white"
            onClick={() => setOpen((s) => !s)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="md:hidden glass-dark border-t border-white/10 rounded-b-3xl"
        >
          <nav className="px-6 py-5 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-lg font-semibold ${isActive ? "text-[#E5B07D]" : "text-white/90"}`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={`tel:${RESTAURANT.phoneRaw}`}
              data-testid="mobile-call-link"
              className="text-base font-medium text-white/70 mt-2 inline-flex items-center gap-2"
            >
              <Phone className="w-4 h-4" /> {RESTAURANT.phone}
            </a>
            <button
              data-testid="mobile-rewards-btn"
              onClick={() => {
                setRewardsOpen(true);
                setOpen(false);
              }}
              className="text-base font-medium text-[#E5B07D] inline-flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" /> My Rewards
            </button>
          </nav>
        </div>
      )}
      <RewardsModal open={rewardsOpen} onClose={() => setRewardsOpen(false)} />
    </header>
  );
}
